import { Box } from '@mui/material';
import { RisksType, RISK_STATUS, RISK_TYPE } from 'domain/shared/newProject';
import RiskSection from '../../commercial/sections/RiskSection';
import RequiredActionsSection from '../../commercial/sections/RequiredActionsSection';
import { EMPTY_MESSAGES, SECTION_TITLES } from '../../commercial/constants';
import ProductionIncidentsSection from './ProductionIncidentsSection';
import ProductionBugsSection from './ProductionBugsSection';
import { ProductionOwnership } from 'domain/projects/newProject/engineering/types';
import { Features } from 'infrastructure/featureToggle';
import useFeatureToggle from 'framework/featureToggle';
import { ControlData } from '../../types';
import { useEngineeringRequiredActions } from './requiredActions';
import { getOutdatedRisks } from '../../utils';

type EngineeringRisksProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
};

const EngineeringSections = ({ data, refetchControlsData }: EngineeringRisksProps) => {
  const allRisks = data?.risks || [];
  const { isFeatureEnabled } = useFeatureToggle();

  const requiredActions = useEngineeringRequiredActions({ data, refetchControlsData });

  const activeRisks = allRisks.filter(
    (risk: RisksType) => risk.type === RISK_TYPE.ENGINEERING && risk.status === RISK_STATUS.ACTIVE,
  );

  const escalatedRisks = activeRisks.filter((risk: RisksType) => risk.isEscalated);
  const otherRisks = activeRisks.filter((risk: RisksType) => !risk.isEscalated);
  const outdatedRisks = getOutdatedRisks(activeRisks);

  return (
    <Box sx={{ px: 2, py: 1 }}>
      {isFeatureEnabled(Features.NEW_PROJECT_ENGINEERING) && requiredActions.length > 0 && (
        <RequiredActionsSection title={SECTION_TITLES.REQUIRED_ACTIONS} actions={requiredActions} />
      )}
      <RiskSection
        title={SECTION_TITLES.ESCALATED_ENGINEERING_RISKS}
        risks={escalatedRisks}
        emptyMessage={EMPTY_MESSAGES.ESCALATED_RISKS}
      />
      <RiskSection
        title={SECTION_TITLES.OTHER_ENGINEERING_RISKS}
        risks={otherRisks}
        emptyMessage={EMPTY_MESSAGES.OTHER_RISKS}
      />
      {outdatedRisks.length > 0 && (
        <RiskSection
          title={SECTION_TITLES.OUTDATED_RISKS}
          risks={outdatedRisks}
          emptyMessage={EMPTY_MESSAGES.OTHER_RISKS}
        />
      )}
      {data?.engineeringPolicy?.productionOwnership === ProductionOwnership.OUR_TEAM &&
        isFeatureEnabled(Features.NEW_PROJECT_ENGINEERING) && (
          <>
            <ProductionIncidentsSection
              incidents={data?.engineeringIncidents}
              title={SECTION_TITLES.PRODUCTION_INCIDENTS}
              emptyMessage={EMPTY_MESSAGES.PRODUCTION_INCIDENTS}
            />
            <ProductionBugsSection
              bugs={data?.engineeringBugs}
              title={SECTION_TITLES.PRODUCTION_BUGS}
              emptyMessage={EMPTY_MESSAGES.PRODUCTION_BUGS}
            />
          </>
        )}
    </Box>
  );
};

export default EngineeringSections;
